import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";
import { Button, ConfirmModal, Input, Card, CardContent } from "@/components/ui";
import {
  DataTable,
  DataTablePrimaryText,
  createActionsColumn,
  type DataTableColumn,
} from "@/components/table";
import {
  devicePresetKeys,
  devicePresetService,
} from "@/services/devicePresetService";
import { getApiErrorMessage, toastApiError } from "@/lib/errorMessages";
import type { DevicePreset, DevicePresetCreate } from "@/types";
import { Permission } from "@/types";
import { useAuthStore } from "@/store/authStore";

const EMPTY_FORM: DevicePresetCreate = {
  brand: "",
  model: "",
  android_version: "",
  display_resolution: "",
  display_inches: 0,
};

type SortColumn =
  "brand" | "model" | "android" | "resolution" | "inches";

export default function DevicePresetsPage() {
  const { t } = useTranslation("devices");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const queryClient = useQueryClient();
  const canHardDelete = useAuthStore(
    (s) =>
      s.hasPermission(Permission.PLATFORM_HARD_DELETE) &&
      s.hasPermission(Permission.DEVICES_PRESETS_UPDATE),
  );
  const [editingPresetId, setEditingPresetId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DevicePreset | null>(null);
  const [form, setForm] = useState<DevicePresetCreate>(EMPTY_FORM);

  const {
    data: presets = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: devicePresetKeys.lists(),
    queryFn: () => devicePresetService.getAll(),
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: DevicePresetCreate) => {
      if (editingPresetId) {
        return devicePresetService.update(editingPresetId, payload);
      }
      return devicePresetService.create(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: devicePresetKeys.lists() });
      toast.success(
        editingPresetId ? t("presets.updated") : t("presets.created"),
      );
      setEditingPresetId(null);
      setForm(EMPTY_FORM);
    },
    onError: (err) => {
      toastApiError(err, "savePreset");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (presetId: string) => devicePresetService.delete(presetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: devicePresetKeys.lists() });
      setPendingDelete(null);
      toast.success(t("presets.deleted"));
    },
    onError: (err) => {
      toastApiError(err, "deletePreset");
    },
  });

  const resetForm = () => {
    setEditingPresetId(null);
    setForm(EMPTY_FORM);
  };

  const validateForm = () => {
    if (
      !form.brand.trim() ||
      !form.android_version.trim() ||
      !form.display_resolution.trim()
    ) {
      toast.error(t("presets.validationRequired"));
      return false;
    }
    if (!Number.isFinite(form.display_inches) || form.display_inches <= 0) {
      toast.error(t("presets.validationDisplayInches"));
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    saveMutation.mutate({
      brand: form.brand.trim(),
      model: (form.model ?? "").trim() || undefined,
      android_version: form.android_version.trim(),
      display_resolution: form.display_resolution.trim(),
      display_inches: form.display_inches,
    });
  };

  const handleEdit = (preset: DevicePreset) => {
    setEditingPresetId(preset.id);
    setForm({
      brand: preset.brand,
      model: preset.model ?? "",
      android_version: preset.android_version,
      display_resolution: preset.display_resolution,
      display_inches: preset.display_inches,
    });
  };

  const loadError =
    error != null ? getApiErrorMessage(error, "loadDevicePresets") : null;

  const columns = useMemo<DataTableColumn<DevicePreset, SortColumn>[]>(
    () => [
      {
        id: "brand",
        header: t("shared.brand"),
        sortKey: "brand",
        sortKind: "text",
        getSortValue: (row) => row.brand,
        defaultWidth: 140,
        cell: (preset) => (
          <DataTablePrimaryText>{preset.brand}</DataTablePrimaryText>
        ),
      },
      {
        id: "model",
        header: t("presets.colModel"),
        sortKey: "model",
        sortKind: "text",
        getSortValue: (row) => row.model,
        defaultWidth: 140,
        cell: (preset) => preset.model || "—",
      },
      {
        id: "android",
        header: t("presets.colAndroid"),
        sortKey: "android",
        sortKind: "text",
        getSortValue: (row) => row.android_version,
        defaultWidth: 120,
        cell: (preset) => preset.android_version,
      },
      {
        id: "resolution",
        header: t("presets.colResolution"),
        sortKey: "resolution",
        sortKind: "text",
        getSortValue: (row) => row.display_resolution,
        defaultWidth: 140,
        cell: (preset) => preset.display_resolution,
      },
      {
        id: "inches",
        header: t("presets.colInches"),
        sortKey: "inches",
        sortKind: "number",
        getSortValue: (row) => row.display_inches,
        defaultWidth: 100,
        cell: (preset) =>
          t("presets.inchesValue", { inches: preset.display_inches }),
      },
      createActionsColumn(t("presets.colActions"), {
        cell: (preset) => (
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => handleEdit(preset)}
              aria-label={t("presets.editTitle")}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {canHardDelete ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => setPendingDelete(preset)}
                className="text-error-700 hover:bg-error-50 hover:text-error-800"
                aria-label={t("presets.deleteTitle")}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        ),
      }),
    ],
    [canHardDelete, t],
  );

  return (
    <div className="space-y-6">
      <Card className="animate-slide-up">
        <CardContent className="px-5 py-4">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 xl:flex-row xl:items-center"
      >
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Input
            type="text"
            value={form.brand}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, brand: e.target.value }))
            }
            placeholder={t("presets.brandPlaceholder")}
            aria-label={t("presets.brandPlaceholder")}
          />
          <Input
            type="text"
            value={form.model ?? ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, model: e.target.value }))
            }
            placeholder={t("presets.modelPlaceholder")}
            aria-label={t("presets.modelPlaceholder")}
          />
          <Input
            type="text"
            value={form.android_version}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, android_version: e.target.value }))
            }
            placeholder={t("presets.androidVersionPlaceholder")}
            aria-label={t("presets.androidVersionPlaceholder")}
          />
          <Input
            type="text"
            value={form.display_resolution}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                display_resolution: e.target.value,
              }))
            }
            placeholder={t("presets.resolutionPlaceholder")}
            aria-label={t("presets.resolutionPlaceholder")}
          />
          <Input
            type="number"
            min="0.1"
            step="0.1"
            value={form.display_inches || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                display_inches: Number.parseFloat(e.target.value) || 0,
              }))
            }
            placeholder={t("presets.inchesPlaceholder")}
            aria-label={t("presets.inchesPlaceholder")}
          />
        </div>
        <div className="flex shrink-0 justify-end gap-2">
          {editingPresetId && (
            <Button
              type="button"
              variant="outline"
              onClick={resetForm}
            >
              {tCommon("cancel")}
            </Button>
          )}
          <Button
            type="submit"
            isLoading={saveMutation.isPending}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            {editingPresetId ? t("presets.save") : t("presets.add")}
          </Button>
        </div>
      </form>
        </CardContent>
      </Card>

      <DataTable
        labels={tableLabels}
        closeLabel={tCommon("close")}
        loadingLabel={tCommon("loading")}
        rows={presets}
        columns={columns}
        getRowKey={(row) => row.id}
        defaultSortColumn="brand"
        preferencesKey="device-presets"
        loadState={
          isLoading
            ? { status: "loading" }
            : loadError != null
              ? {
                  status: "error",
                  message: loadError,
                  retryLabel: tCommon("tryAgain"),
                  onRetry: () => void refetch(),
                }
              : undefined
        }
        emptyMessage={t("presets.empty")}
      />

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
       
        isOpen={pendingDelete != null}
        onClose={() => {
          if (!deleteMutation.isPending) setPendingDelete(null);
        }}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        title={t("presets.deleteTitle")}
        message={t("presets.deleteConfirm", {
          name: pendingDelete
            ? [pendingDelete.brand, pendingDelete.model]
                .filter(Boolean)
                .join(" ")
            : "",
        })}
        confirmText={tCommon("delete")}
        isDestructive
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
